import { useEntities } from 'utils/hooks/api';
import {
    Alert,
    AlertDescription,
    AlertTitle,
    AnimatedSpateAiIcon,
} from '@/ui/design-system/components';
import { AlertCircle, ChevronRight, ListFilter } from 'lucide-react';
import {
    CardsWrapper,
    defaultFilterAccessorsAndOptions,
    useCardsFilterSort,
} from 'components/CardsWrapper';
import { DATA_SOURCES, TIKTOK_COUNTRIES, isCovered } from '@/config/constants';
import {
    DataSourceBreadCrumbs,
    DataSourceIndustriesOnlyBreadCrumbs,
} from 'components';
import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import {
    Redirect,
    generatePath,
    useHistory,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadPopularityTimeSeriesAsCSV,
    getHierarchyCSVColumns,
} from 'utils/formatCSV';
import {
    filterClusters,
    filterHealthStats,
    filterIds,
    filterLifeCycle,
    filterNames,
} from 'utils/filters';
import {
    useAreFlavorFiltersSupported,
    useCurrentCountry,
    useEndDate,
    useHandleAreFlavorFiltersSupported,
    useIsAdmin,
    useSetupFlavorFilters,
    useTikTokWeeks,
} from 'utils/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BodyMedium } from 'components/Text';
import { Button } from '@/ui/design-system/components/ui/button';
import { CardSkeleton } from 'components/CardSkeleton';
import CatFourFilterList from 'components/FlavorFilters/CatFourFilterList';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import Drawer from 'components/Drawer';
import FakeProgressBar from 'components/FakeProgressBar';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoDataSourceMarketCoverage from 'components/NoDataSourceMarketCoverage';
import OverviewCardV2, {
    hasRow2Filter,
} from 'components/OverviewCard/OverviewCardV2';
import OverviewDrawerV2 from 'components/OverviewDrawerV2';
import OverviewV2CardsFilterAccordion from 'components/OverviewV2CardsFilterAccordion';
import { POPULARITY_SORT_OPTIONS } from 'utils/metrics';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import SearchInput from 'components/DataVis/DataTable/SearchInput';
import { SoftButton } from 'components/Button';
import SortSelect from 'components/Input/SortSelect';
import Stack from 'components/Stack';
import { cn } from '@/ui/design-system/utils/cn';
import { getHierarchy } from 'utils/hooks/useGetHierarchy';
import { mixpanelTrack } from 'utils/app';
import useCountryParam from 'utils/hooks/useCountryParam';
import usePopularity from 'utils/hooks/usePopularity';

const PADDING_SIZE = 20;

const defaultHandleFilter = {
    cluster: filterClusters,
    life_cycle: filterLifeCycle,
    healthStats: filterHealthStats,
    name: filterNames,
    id: filterIds,
};

export function transformDataForTimeSeriesCSV(sortedData) {
    return sortedData.reduce(
        (
            acc,
            {
                timeSeriesGoogle,
                timeSeriesTikTok,
                timeSeriesInstagram,
                timeSeriesOverview,
                timeSeriesCombined,
                paginateItem,
            },
        ) => {
            const tsData = [];
            if (timeSeriesGoogle && timeSeriesGoogle.length)
                tsData.push({ id: 'Google', timeSeries: timeSeriesGoogle });
            if (timeSeriesTikTok && timeSeriesTikTok.length)
                tsData.push({ id: 'TikTok', timeSeries: timeSeriesTikTok });
            if (timeSeriesInstagram && timeSeriesInstagram.length)
                tsData.push({
                    id: 'Instagram',
                    timeSeries: timeSeriesInstagram,
                });
            if (timeSeriesOverview && timeSeriesOverview.length)
                tsData.push({ id: 'overview', timeSeries: timeSeriesOverview });
            if (timeSeriesCombined && timeSeriesCombined.length)
                tsData.push({ id: 'combined', timeSeries: timeSeriesCombined });
            if (tsData.length) acc.push({ name: paginateItem?.name, tsData });
            return acc;
        },
        [],
    );
}

export function createDrawerDetails({
    paginateItem,
    data,
    timeSeriesGoogle,
    timeSeriesTikTok,
    timeSeriesInstagram,
    timeSeriesCombined,
    parentCategory,
}) {
    return {
        paginateItem,
        data,
        parentCategory,
        timeSeriesGoogle,
        timeSeriesTikTok,
        timeSeriesInstagram,
        timeSeriesCombined,
    };
}

function createCSVDownloadOptions({
    parentCategory,
    country,
    date,
    sortedData,
    exportFields,
    trendType,
}) {
    const csvExportFields = exportFields.filter((field) => field.key !== 'id');
    const baseParams = {
        entity: parentCategory?.name,
        country,
        source: 'pop-index',
        type: trendType === 'brands' ? 'brand' : 'trend',
        date,
    };
    return [
        {
            mixpanelMeta: { label: 'card data csv' },
            children: 'Card Data CSV',
            onClick: () => {
                const csvData = sortedData.map((item) => {
                    const { parent, hierarchy_hint } = getHierarchy(
                        item.hierarchies,
                    );
                    return hierarchy_hint
                        ? { ...item, hierarchies: [[parent, hierarchy_hint]] }
                        : item;
                });
                downloadItemsAsCSV(
                    buildExportFilename({ ...baseParams, kind: 'data' }),
                    csvData,
                    csvExportFields,
                );
            },
            downloadType: downloadTypes.CSV,
            key: 'card data csv',
        },
        {
            mixpanelMeta: { label: 'time series csv' },
            children: 'Time Series CSV',
            onClick: () =>
                downloadPopularityTimeSeriesAsCSV(
                    buildExportFilename({
                        ...baseParams,
                        kind: 'time-series',
                    }),
                    transformDataForTimeSeriesCSV(sortedData),
                ),
            downloadType: downloadTypes.CSV,
            key: 'time series csv',
        },
    ];
}

const mixpanelMeta = { contentKey: 'OverviewCards' };

const sortOptions = [...Object.values(POPULARITY_SORT_OPTIONS)];
const defaultSortOptions = [
    POPULARITY_SORT_OPTIONS.popularity_index_avg_12_month,
    POPULARITY_SORT_OPTIONS.YoY,
    POPULARITY_SORT_OPTIONS.increase_YoY,
    POPULARITY_SORT_OPTIONS.QoQ,
    POPULARITY_SORT_OPTIONS.increase_QoQ,
    POPULARITY_SORT_OPTIONS.MoM,
    POPULARITY_SORT_OPTIONS.increase_MoM,
];
const defaultInitialSortOption = defaultSortOptions[0];

function FiltersToggleButton({ isOpen, onClick }) {
    return (
        <div className="inline-flex items-center" onClick={onClick}>
            <Button
                variant="outline"
                size="icon"
                className="rounded-r-none border-r-0"
            >
                <ChevronRight
                    className={cn(
                        'transition-transform',
                        isOpen && 'rotate-180',
                    )}
                />
            </Button>
            <Button variant="outline" className="rounded-l-none pl-2 pr-4">
                <ListFilter />
                Filters
            </Button>
        </div>
    );
}

const OverviewCards = ({
    categoryId,
    trendType,
    trendDimension,
    country,
    name,
    parentCategory,
    categoryQuery,
}) => {
    const isAdmin = useIsAdmin();
    const healthStatKeys = [
        'average monthly popularity',
        'popularity share',
        'popularity convergence',
        'popularity predicted growth',
        ...(trendType === 'brands' ? ['price point'] : []),
    ];
    const { week, year } = useTikTokWeeks();
    const date = useEndDate('pop-index');
    const {
        handleFilter,
        filterAccessorsAndOptions,
        handleFiltersWithoutCatFours,
    } = useSetupFlavorFilters({
        defaultHandleFilter,
        defaultFilterAccessorsAndOptions,
    });
    // Brands are filtered by the category itself; subcategory rows are every
    // cat5 beneath it, which the pop index expands against its own taxonomy.
    const isSubcategoryTrends = trendType !== 'brands';
    const categoryScope = categoryId ? [categoryId] : null;
    const categories = isSubcategoryTrends ? null : categoryScope;
    const categoryDescendants = isSubcategoryTrends ? categoryScope : null;
    const isViewingCategories =
        trendType === 'cat_fives' || trendType === 'categories';

    const brands = trendType === 'brands' ? '*' : null;
    const overviewDataQuery = usePopularity({
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        paginateBy: trendDimension,
        withPricePoint: trendType === 'brands',
        categories,
        categoryDescendants,
        brands,
        countries: country ? [country.id] : null,
        enabled: !!(
            week &&
            year &&
            country &&
            (categories || categoryDescendants || brands)
        ),
    });
    const isFirstPageLoaded = !!overviewDataQuery.firstPageQuery?.isSuccess;
    const areMorePagesLoading = !!overviewDataQuery.areMorePagesLoading;
    const isAllOverviewDataLoaded = isFirstPageLoaded && !areMorePagesLoading;
    const cardData = useMemo(
        () =>
            overviewDataQuery.data?.filter(
                ({ fetching, fetchingData }) => !fetching && !fetchingData,
            ) ?? overviewDataQuery.data,
        [overviewDataQuery.data],
    );
    const {
        sortedData,
        filters,
        setFilter,
        setFilters,
        resetFilters,
        sortOption,
        setSortOption,
        initialSortOption,
        onSearchInputChange,
    } = useCardsFilterSort({
        rootCategoryId: categoryId,
        data: cardData,
        initialSortOption: defaultInitialSortOption,
        handleFilter,
        sortOptions,
        filterAccessorsAndOptions,
    });

    const healthStatFilters = filters?.healthStats;
    const clusterFilters = filters?.cluster;
    const hasRow2 = useMemo(
        () =>
            hasRow2Filter({
                healthStats: healthStatFilters,
                cluster: clusterFilters,
            }),
        [healthStatFilters, clusterFilters],
    );
    // +22 over the one-line header, so the sparkline keeps the 96px it had.
    const cardRowHeight = hasRow2 ? 322 : 272;

    const flavorFiltersSupported = useAreFlavorFiltersSupported({
        categoryQuery,
    });
    const areFlavorFiltersSupported =
        trendDimension === 'categories' && flavorFiltersSupported;
    const exportFields = [
        { key: 'id', label: 'ID' },
        { key: 'name' },
        { key: 'life_cycle', label: 'life cycle' },
        {
            key: 'popularity_index_avg_12_month',
            label: 'avg. monthly popularity',
        },
        { key: 'YoY' },
        { key: 'increase_YoY', label: 'increase YoY' },
        { key: 'QoQ' },
        { key: 'increase_QoQ', label: 'increase QoQ' },
        { key: 'MoM' },
        { key: 'increase_MoM', label: 'increase MoM' },
        { key: 'predicted_YoY', label: 'predicted YoY' },
        { key: 'google_share_prc', label: 'Google share %' },
        { key: 'tiktok_share_prc', label: 'TikTok share %' },
        { key: 'instagram_share_prc', label: 'Instagram share %' },
        { key: 'search_YoY', label: 'Google YoY' },
        { key: 'tiktok_YoY', label: 'TikTok YoY' },
        { key: 'instagram_YoY', label: 'Instagram YoY' },
        { key: 'cluster' },
        { key: 'healthStats' },
        ...(areFlavorFiltersSupported ? [{ key: 'flavors' }] : []),
        ...(trendDimension === 'categories'
            ? getHierarchyCSVColumns(isAdmin)
            : []),
    ];
    const [details, setDetails] = useState(null);
    const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(true);
    const onCardClick = ({
        paginateItem,
        data,
        timeSeriesGoogle,
        timeSeriesTikTok,
        timeSeriesInstagram,
    }) =>
        setDetails({
            paginateItem,
            data,
            parentCategory,
            timeSeriesGoogle,
            timeSeriesTikTok,
            timeSeriesInstagram,
        });

    const defaultSearchInputValue =
        filters.name && filters.name[0] ? filters.name[0] : '';

    const activeFilterCount = Object.entries(filters).reduce(
        (acc, [key, value]) => {
            if (key === 'id' || !value) return acc;
            return acc + value.length;
        },
        0,
    );
    const totalClearableCount = activeFilterCount;
    const handleClearAll = () => {
        resetFilters();
    };

    let noDataMessage;

    if (!categoryId) {
        noDataMessage = 'Choose an Industry';
    } else if (overviewDataQuery.data && overviewDataQuery.data.length > 0) {
        noDataMessage = `No ${trendType === 'cat_fives' ? 'subcategories' : trendType} available. Try adjusting the filters.`;
        // Resolving the category is what says this page has something to show.
        // This read `volume` off a Google data row, which is neither this page's
        // source nor available now that the entity is fetched as an entity.
    } else if (!categoryQuery.data?.[0]) {
        noDataMessage = `No ${trendType === 'cat_fives' ? 'subcategories' : trendType} to display. Coverage may not be available for this region.`;
    } else {
        noDataMessage = `No ${trendType === 'cat_fives' ? 'subcategories' : trendType} available.`;
    }

    const dataReady =
        overviewDataQuery.data &&
        overviewDataQuery.data.length > 0 &&
        !overviewDataQuery.isLoading;

    const isLoading = !isFirstPageLoaded;

    if (
        parentCategory?.hierarchies?.[0]?.[1] &&
        country &&
        !isCovered({
            geo: country?.text_id,
            dataSource: DATA_SOURCES.POPULARITY,
            cat1: parentCategory?.hierarchies?.[0]?.[1]?.cat1,
        })
    )
        return (
            <NoDataSourceMarketCoverage dataSource={DATA_SOURCES.POPULARITY} />
        );

    return (
        <>
            <Stack
                $space="8px"
                $direction="row"
                css={`
                    display: flex;
                    justify-content: space-between;
                    flex-wrap: nowrap;
                    margin-bottom: 16px;
                `}
            >
                <SearchInput
                    // TODO: support multiple name filters
                    defaultValue={defaultSearchInputValue}
                    onSearchInputChange={onSearchInputChange}
                    name={name}
                    mixpanelMeta={mixpanelMeta}
                />
                <SortSelect
                    options={defaultSortOptions}
                    value={sortOption}
                    onChange={(selected) => setSortOption(selected)}
                    mixpanelContext="CardsSortSelect"
                    buttonProps={{ size: 'default' }}
                />
                <DownloadCSVMenuButton
                    isDisabled={!dataReady}
                    isLoading={overviewDataQuery.isLoading}
                    options={createCSVDownloadOptions({
                        parentCategory,
                        country,
                        date,
                        sortedData,
                        exportFields,
                        trendType,
                    })}
                    mixpanelMeta={mixpanelMeta}
                    buttonProps={{ size: 'default' }}
                />
            </Stack>
            <div
                css={`
                    display: grid;
                    grid-template-columns: ${
                        isFiltersPanelOpen ? '287px 1fr' : '1fr'
                    };
                    grid-column-gap: 12px;
                    padding-bottom: 240px;
                `}
            >
                {isFiltersPanelOpen && (
                    <div css="position: sticky; top: 0; align-self: start;">
                        <OverviewV2CardsFilterAccordion
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            sortOption={sortOption}
                            setSortOption={setSortOption}
                            initialSortOption={initialSortOption}
                            mixpanelMeta={mixpanelMeta}
                            healthStatKeys={healthStatKeys}
                            areFlavorFiltersSupported={
                                areFlavorFiltersSupported
                            }
                            data={overviewDataQuery.data}
                            isLoadingData={!isAllOverviewDataLoaded}
                        />
                    </div>
                )}
                {/* 1fr cols don't shrink smaller than their content unless width explicitly set */}
                <div css="min-width: 0;">
                    <div
                        css={`
                            margin-bottom: 10px;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        `}
                    >
                        <FiltersToggleButton
                            isOpen={isFiltersPanelOpen}
                            onClick={() =>
                                setIsFiltersPanelOpen((prev) => !prev)
                            }
                        />
                        {!isLoading && (
                            <>
                                <span className="text-sm font-medium truncate">
                                    Results
                                </span>
                                <span className="text-sm text-muted-foreground">
                                    {sortedData?.length}
                                </span>
                            </>
                        )}
                        {totalClearableCount > 0 && (
                            <SoftButton onClick={handleClearAll}>
                                Clear {totalClearableCount} filter
                                {totalClearableCount > 1 ? 's' : ''}
                            </SoftButton>
                        )}
                    </div>
                    <CatFourFilterList
                        filters={filters}
                        setFilter={setFilter}
                        gridContainerCss={`margin: 14px 9px 10px;`}
                        $placeholderMargin={'10px 9px 0px'}
                        isLoading={!isAllOverviewDataLoaded}
                        data={overviewDataQuery.data}
                        filterAccessorsAndOptions={filterAccessorsAndOptions}
                        handleFiltersWithoutCatFours={
                            handleFiltersWithoutCatFours
                        }
                    />
                    {overviewDataQuery.hasErroredPages && (
                        <Alert variant="destructive" className="mb-3">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>
                                Some results couldn&apos;t be loaded
                            </AlertTitle>
                            <AlertDescription className="flex items-center justify-between gap-3">
                                <span>
                                    Part of the dataset failed to fetch. Retry
                                    to load the missing items.
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        overviewDataQuery.restPageQueries
                                            .filter((q) => q.isError)
                                            .forEach((q) => q.refetch())
                                    }
                                >
                                    Retry
                                </Button>
                            </AlertDescription>
                        </Alert>
                    )}
                    {areMorePagesLoading && !isLoading && (
                        <div
                            role="status"
                            aria-live="polite"
                            className="mb-3 flex items-center gap-3 pl-[6px]"
                        >
                            <AnimatedSpateAiIcon size={18} />
                            <BodyMedium>Loading more results…</BodyMedium>
                        </div>
                    )}
                    <CardsWrapper
                        key={categoryId}
                        data={sortedData}
                        filters={filters}
                        sortOption={sortOption}
                        noDataMessage={noDataMessage}
                        isLoading={isLoading}
                        onCardClick={onCardClick}
                        CardComponent={OverviewCardV2}
                        LoadingComponent={
                            isViewingCategories ? CardSkeleton : FakeProgressBar
                        }
                        isViewingCategories={isViewingCategories}
                        parentCategory={parentCategory}
                        rowHeight={cardRowHeight}
                        skeletonCount={
                            areMorePagesLoading && sortedData?.length > 0
                                ? 12
                                : 0
                        }
                    />
                </div>
                <Drawer open={!!details} onClose={() => setDetails(null)}>
                    {details && <OverviewDrawerV2 {...details} />}
                </Drawer>
            </div>
        </>
    );
};

const ExploreOverview = () => {
    const country = useCurrentCountry();
    const { mutateToWithCountryParam } = useCountryParam();
    const location = useLocation();
    const history = useHistory();
    const { path } = useRouteMatch();
    const { breakdown, type, categoryId } = useParams();
    const hasInitializedDefaultFilter = useRef(false);
    const trendType = type === 'trends' ? 'cat_fives' : type;
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { breakdown, type, categoryId: id }),
        search: location.search,
    });
    const categoryQuery = useEntities({
        enabled: !!categoryId,
        ids: [categoryId],
        entityType: 'categories',
        dataSource: DATA_SOURCES.POPULARITY,
    });

    const name = categoryQuery.data?.[0]?.name ?? null;

    const trendDimension = trendType === 'cat_fives' ? 'categories' : trendType;

    useEffect(() => {
        if (name && country) {
            const eventName =
                trendType === 'brands'
                    ? 'Overview Brand Trends'
                    : 'Overview Category Trends';

            mixpanelTrack(eventName, { name, countryName: country.name });
        }
    }, [name, country, trendType]);

    // Apply the default `?life_cycle=growth` filter only on initial landing.
    // Running this in an effect (rather than a render-time `<Redirect>`) keeps
    // the Clear button working — once the user has interacted, the ref guard
    // prevents the default from re-applying when the URL is empty.
    useEffect(() => {
        if (hasInitializedDefaultFilter.current) return;
        if (!categoryId) return;
        hasInitializedDefaultFilter.current = true;
        if (!location.search || location.search === '') {
            const params = new URLSearchParams();
            params.set('life_cycle', 'growth');
            params.set('sort', POPULARITY_SORT_OPTIONS.increase_YoY.value);
            history.replace({
                pathname: location.pathname,
                search: `?${params.toString()}`,
            });
        }
    }, [categoryId]);

    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    if (isNotTikTokMarket)
        return (
            <Redirect
                to={mutateToWithCountryParam(
                    `/explore/google/${type}${categoryId ? `/${categoryId}` : ''}`,
                )}
            />
        );

    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.5rem">
                <div className="relative z-[1]">
                    {type !== 'brands' ? (
                        <DataSourceBreadCrumbs
                            dataSource="POPULARITY"
                            cat1={
                                categoryQuery.data?.[0]?.hierarchies?.[0]?.cat1
                            }
                            geo={country?.text_id}
                            showIndustriesMenu={true}
                            generateLocation={generateLocation}
                            maxDepth={4}
                            replace={true}
                        />
                    ) : !categoryId ? (
                        <DataSourceIndustriesOnlyBreadCrumbs
                            dataSource="POPULARITY"
                            geo={country?.text_id}
                            generateLocation={generateLocation}
                            showMetrics={false}
                        />
                    ) : (
                        // DataSourceBreadCrumbs uses an auto-fit grid that stretches
                        // to fill its parent. With at most 2 crumbs (cat1 + cat2) we
                        // constrain to roughly half-width so it doesn't span the page.
                        <div
                            css={`
                                max-width: calc((100% - 120px) / 2);
                            `}
                        >
                            <DataSourceBreadCrumbs
                                dataSource="POPULARITY"
                                cat1={
                                    categoryQuery.data?.[0]
                                        ?.hierarchies?.[0]?.[1]?.cat1
                                }
                                geo={country?.text_id}
                                showIndustriesMenu={true}
                                generateLocation={generateLocation}
                                maxDepth={2}
                                replace={true}
                                skeletonSize={2}
                            />
                        </div>
                    )}
                </div>
                <DataSourceTabsByCoverage
                    entityType={type === 'trends' ? 'category' : 'brand'}
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={
                        categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]
                    }
                    customMode={true}
                    customOverviewTo={location}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/${type}${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={mutateToWithCountryParam(
                        `/explore/tiktok/${type}${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/${type}${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
                {categoryId ? (
                    <OverviewCards
                        categoryId={categoryId}
                        trendType={trendType}
                        trendDimension={trendDimension}
                        country={country}
                        name={name}
                        parentCategory={categoryQuery.data?.[0]}
                        categoryQuery={categoryQuery}
                    />
                ) : (
                    <IndustriesPlaceholderMessage />
                )}
            </Stack>
        </PagePadding>
    );
};

export default ExploreOverview;
